import React, { ReactNode, useEffect, useState } from 'react';

import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@material-ui/core';
import EmailIcon from '@material-ui/icons/Email';

import { InlineForm } from '@sierralabs/react-material-ui-formik';
import AppSnackbar, { AppSnackbarProps } from '../../../common/AppSnackbar';
import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineHtml from '../../../common/inline/InlineHtml';
import InlineRadioGroup from '../../../common/inline/InlineRadioGroup';
import InlineTextField from '../../../common/inline/InlineTextField';
import { useSendTrainingReminder } from '../Training.hooks';
import {
  BaseTrainingCardProps,
  TrainingReminderType,
  TrainingSendReminderDto
} from '../Training.interface';

const initialValues: TrainingSendReminderDto = {
  type: TrainingReminderType.Incomplete,
  subject: '',
  message: ''
};

const TrainingSendEmailReminderCard: React.FC<BaseTrainingCardProps> = ({
  training
}) => {
  const [open, setOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    severity: AppSnackbarProps['severity'];
    message?: ReactNode;
  }>({ open: false, severity: 'success' });
  const [emailReminder, setEmailReminder] = useState(initialValues);
  const { data, isLoading, error, sendReminder } = useSendTrainingReminder(
    training.id
  );

  const handleClose = () => !isLoading && setOpen(false);

  useEffect(() => {
    if (data) {
      const severity: AppSnackbarProps['severity'] =
        data.success === data.total
          ? 'success'
          : data.success > 0 && data.success !== data.total
          ? 'warning'
          : 'error';

      if (severity !== 'error') {
        setOpen(false);
      }

      setSnackbar({
        severity,
        open: true,
        message:
          severity === 'success' ? (
            'Training reminders sent successfully.'
          ) : severity === 'warning' ? (
            <>
              <strong>{data.success}</strong> of <strong>{data.total}</strong>{' '}
              reminders sent successfully.
            </>
          ) : (
            'An error occurred while sending training reminders.'
          )
      });
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      setSnackbar({ open: true, severity: 'error', message: error.message });
    }
  }, [error]);

  useEffect(() => {
    if (!open) {
      setEmailReminder(initialValues);
    }
  }, [open]);

  return (
    <>
      <Card>
        <CardTitle>
          <EmailIcon />
          Send Email Reminder
        </CardTitle>
        <CardContent padding='small'>
          <Box mb={2}>
            <Typography display='block' variant='caption'>
              Send email reminder for trainees about this{' '}
              <strong>latest approved training version</strong> whether the
              training is overdue or if the training is still incomplete.
            </Typography>
          </Box>
          <Button
            variant='outlined'
            color='primary'
            onClick={() => setOpen(true)}
          >
            Send Email Reminder
          </Button>
        </CardContent>
      </Card>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Send Email Reminder</DialogTitle>
        <DialogContent>
          <InlineFormik
            isSubmitting={false}
            initialValues={emailReminder}
            onSubmit={values =>
              setEmailReminder(emailReminder => {
                return { ...emailReminder, ...values };
              })
            }
          >
            <InlineForm>
              <InlineRadioGroup
                disabled={isLoading}
                label='Reminder Type'
                name='type'
                options={[
                  {
                    label: 'Incomplete Training',
                    value: TrainingReminderType.Incomplete
                  },
                  {
                    label: 'Overdue Training',
                    value: TrainingReminderType.Overdue
                  }
                ]}
              />
              <InlineTextField disabled={isLoading} name='subject' />
              <InlineHtml
                submitOnChange // prevent double clicking to submit the form
                hideConfirm
                disabled={isLoading}
                label='Message'
                name='message'
              />
            </InlineForm>
          </InlineFormik>
        </DialogContent>
        <DialogActions>
          <Button disabled={isLoading} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            disabled={isLoading}
            variant='contained'
            color='primary'
            endIcon={
              isLoading && <CircularProgress color='primary' size={20} />
            }
            onClick={() => sendReminder(emailReminder)}
          >
            Send{isLoading ? 'ing' : ''}
          </Button>
        </DialogActions>
      </Dialog>
      <AppSnackbar
        open={snackbar.open}
        severity={snackbar.severity}
        onClose={() => setSnackbar(snackbar => ({ ...snackbar, open: false }))}
      >
        {snackbar.message}
      </AppSnackbar>
    </>
  );
};

export default TrainingSendEmailReminderCard;
